import { type ReactNode, useState } from 'react';
import { Alert } from '@cmsgov/design-system';

import { DismissibleAlert, type DismissibleAlertVariation } from '../DismissibleAlert';

interface ApplicationInformationBannerProps {
  bannerId: string;
  children: ReactNode;
  heading: string;
  dismissible?: boolean;
  dismissalScope?: 'mount' | 'session';
  onDismiss?: () => void;
  variation?: DismissibleAlertVariation;
  'data-testid'?: string;
}

const DISMISSED_BANNER_STORAGE_PREFIX = 'accesscms:application-information-banner-dismissed';

function isDismissedForSession(bannerId: string) {
  try {
    return sessionStorage.getItem(`${DISMISSED_BANNER_STORAGE_PREFIX}:${bannerId}`) === 'true';
  } catch {
    return false;
  }
}

function dismissForSession(bannerId: string) {
  try {
    sessionStorage.setItem(`${DISMISSED_BANNER_STORAGE_PREFIX}:${bannerId}`, 'true');
  } catch {
    // The in-memory dismissal still works when storage is unavailable.
  }
}

/**
 * Reusable application-wide informational banner.
 *
 * Dismissal can be retained for the component mount or the browser-tab session.
 * A different bannerId is treated as a new notice and is displayed automatically.
 */
export function ApplicationInformationBanner({
  bannerId,
  children,
  heading,
  dismissible = true,
  dismissalScope = 'mount',
  onDismiss,
  variation = 'informational',
  'data-testid': dataTestId = 'application-information-banner',
}: ApplicationInformationBannerProps) {
  const [dismissedBannerId, setDismissedBannerId] = useState<string | null>(null);
  const wasDismissedForSession = dismissalScope === 'session' && isDismissedForSession(bannerId);

  if (dismissedBannerId === bannerId || wasDismissedForSession) {
    return null;
  }

  const dismiss = () => {
    setDismissedBannerId(bannerId);
    if (dismissalScope === 'session') {
      dismissForSession(bannerId);
    }
    onDismiss?.();
  };
  const alertVariation = variation === 'informational' ? undefined : variation;

  if (!dismissible) {
    return (
      <div
        className='application-information-banner'
        data-testid={dataTestId}>
        <Alert
          className='application-information-banner__alert'
          data-testid={`${dataTestId}-alert`}
          heading={heading}
          role={variation === 'error' ? 'alert' : 'status'}
          variation={alertVariation}>
          {children}
        </Alert>
      </div>
    );
  }

  return (
    <div
      className='application-information-banner'
      data-testid={dataTestId}>
      <DismissibleAlert
        className='application-information-banner__alert'
        data-testid={`${dataTestId}-alert`}
        heading={heading}
        variation={variation}
        onDismiss={dismiss}>
        {children}
      </DismissibleAlert>
    </div>
  );
}

export default ApplicationInformationBanner;
